/**
 * 将字节数转换为可读格式（KB, MB, GB等）
 * @param bytes 字节数
 * @param decimals 小数位数
 * @returns 格式化后的字符串
 */
export const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * 货币符号位置枚举
 */
export enum CurrencySymbolPosition {
  PREFIX = 'prefix', // 符号在数字前面
  SUFFIX = 'suffix'  // 符号在数字后面
}

/**
 * 预定义的货币符号
 */
export const CURRENCY_SYMBOLS = {
  EUR: '€', // 欧元
  MXN: '$', // 墨西哥比索
  CLP: '$', // 智利比索
  CNY: '¥', // 人民币
  USD: '$', // 美元
  GBP: '£', // 英镑
  JPY: '¥', // 日元
};

/**
 * 货币格式化配置接口
 */
export interface CurrencyFormatConfig {
  symbol: string;                                         // 货币符号
  position: CurrencySymbolPosition;                       // 符号位置
  decimalPlaces: number;                                  // 小数位数
  thousandsSeparator: string;                             // 千位分隔符
  decimalSeparator: string;                               // 小数分隔符
}

/**
 * 默认货币格式化配置
 */
export const DEFAULT_CURRENCY_CONFIG: CurrencyFormatConfig = {
  symbol: '¥',
  position: CurrencySymbolPosition.PREFIX,
  decimalPlaces: 2,
  thousandsSeparator: ',',
  decimalSeparator: '.'
};

/**
 * 格式化货币值
 * @param value 货币数值
 * @param config 格式化配置
 * @returns 格式化后的货币字符串
 */
export const formatCurrency = (
  value: number | string | undefined | null,
  config: Partial<CurrencyFormatConfig> = {}
): string => {
  // 如果值无效则返回默认值
  if (value === undefined || value === null) return '-';
  
  // 合并配置
  const finalConfig: CurrencyFormatConfig = {
    ...DEFAULT_CURRENCY_CONFIG,
    ...config
  };
  
  // 确保值是数字
  let numValue: number;
  if (typeof value === 'string') {
    numValue = parseFloat(value);
  } else {
    numValue = value;
  }
  
  // 处理无效数字
  if (isNaN(numValue)) return '-';
  
  // 格式化数字部分
  const fixedValue = numValue.toFixed(finalConfig.decimalPlaces);
  
  // 分割整数和小数部分
  const [intPart, decimalPart] = fixedValue.split('.');
  
  // 添加千位分隔符
  const formattedIntPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, finalConfig.thousandsSeparator);
  
  // 组合整数和小数部分
  let formattedValue = formattedIntPart;
  if (finalConfig.decimalPlaces > 0) {
    formattedValue += finalConfig.decimalSeparator + decimalPart;
  }
  
  // 根据货币符号位置添加符号
  if (finalConfig.position === CurrencySymbolPosition.PREFIX) {
    return `${finalConfig.symbol}${formattedValue}`;
  } else {
    return `${formattedValue}${finalConfig.symbol}`;
  }
}; 